import { create } from "zustand";
import {
  createProject,
  deleteProject,
  getActiveProjectId,
  importFileAsNewProject,
  listProjects,
  renameProject,
  switchProject,
  type CreateProjectOptions,
  type ProjectMeta,
} from "../persistence/projects";

type ProjectLibraryState = {
  ready: boolean;
  projects: ProjectMeta[];
  activeProjectId: string | null;
  busy: boolean;
  error: string | null;

  hydrate: (projects: ProjectMeta[], activeId: string) => void;
  refresh: () => Promise<void>;
  create: (options?: CreateProjectOptions) => Promise<ProjectMeta>;
  switchTo: (projectId: string) => Promise<void>;
  rename: (projectId: string, name: string) => Promise<void>;
  remove: (projectId: string) => Promise<void>;
  importAsNew: (file: File, name?: string) => Promise<ProjectMeta>;
};

export const useProjectLibraryStore = create<ProjectLibraryState>(
  (set, get) => ({
    ready: false,
    projects: [],
    activeProjectId: getActiveProjectId(),
    busy: false,
    error: null,

    hydrate: (projects, activeId) => {
      set({
        ready: true,
        projects: [...projects].sort((a, b) => b.updatedAt - a.updatedAt),
        activeProjectId: activeId,
        error: null,
      });
    },

    refresh: async () => {
      const projects = await listProjects();
      const activeProjectId = getActiveProjectId() ?? projects[0]?.id ?? null;
      set({
        projects,
        activeProjectId,
        ready: true,
      });
    },

    create: async (options) => {
      set({ busy: true, error: null });
      try {
        // Save current project first if we have one.
        const currentId = get().activeProjectId;
        if (currentId) {
          const { persistActiveProject } =
            await import("../persistence/projects");
          await persistActiveProject(currentId).catch(() => undefined);
        }
        const created = await createProject(options);
        await get().refresh();
        set({ activeProjectId: created.id, busy: false });
        return created;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "无法创建项目。";
        set({ busy: false, error: message });
        throw error;
      }
    },

    switchTo: async (projectId) => {
      if (projectId === get().activeProjectId) return;
      set({ busy: true, error: null });
      try {
        await switchProject(projectId, get().activeProjectId);
        await get().refresh();
        set({ activeProjectId: projectId, busy: false });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "无法切换项目。";
        set({ busy: false, error: message });
        throw error;
      }
    },

    rename: async (projectId, name) => {
      set({ busy: true, error: null });
      try {
        await renameProject(projectId, name);
        await get().refresh();
        set({ busy: false });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "无法重命名项目。";
        set({ busy: false, error: message });
        throw error;
      }
    },

    remove: async (projectId) => {
      set({ busy: true, error: null });
      try {
        const { nextActiveId } = await deleteProject(projectId);
        await get().refresh();
        set({ activeProjectId: nextActiveId, busy: false });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "无法删除项目。";
        set({ busy: false, error: message });
        throw error;
      }
    },

    importAsNew: async (file, name) => {
      set({ busy: true, error: null });
      try {
        const currentId = get().activeProjectId;
        if (currentId) {
          const { persistActiveProject } =
            await import("../persistence/projects");
          await persistActiveProject(currentId).catch(() => undefined);
        }
        const created = await importFileAsNewProject(file, name);
        await get().refresh();
        set({ activeProjectId: created.id, busy: false });
        return created;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "无法导入为新项目。";
        set({ busy: false, error: message });
        throw error;
      }
    },
  }),
);
